import configs from "../utils/configs";
import QRCode from "qrcode";
import { createBoundingBoxFromObject, getModelOnly } from "../utils/artwork-model-utils";
import { findAncestorWithComponent } from "../utils/scene-graph";
import { getSignedUrl } from "../utils/artwork-utils";

AFRAME.registerComponent("artwork-qr", {
  schema: {
    artworkId: { type: "string" },
    image: { type: "string" },
    frame: { type: "string" },
    artworkInfo: { type: "string" },
    mediumName: { type: "string" },
    scaleX: { default: 1 },
    scaleY: { default: 1 },
    scaleZ: { default: 1 },
    glbModel: { type: "string" },
    completed: { type: "boolean", default: false },
    remoteArtwork: { type: "string" },
    qrCodeUrl: { type: "string", default: "" },
    qrCodeId: { type: "string", default: "" },
    resizeOnInit: { type: "boolean", default: false },
    initialScaleX: { type: "number" },
    initialScaleY: { type: "number" },
    initialScaleZ: { type: "number" },
    scaleFactorX: { type: "number" },
    scaleFactorY: { type: "number" },
    scaleFactorZ: { type: "number" },
    myMediaId: { type: "number" },
    isDigitalFile: { type: "boolean" },
    hideArtworkLabel: { type: "boolean", default: false }
  },
  init() {
    this.qrCodeUrl = this.data.qrCodeDataUrl;
    this.initialized = false;
    this.arURL = "";
    this.loaderEl = null;
    this.qrCodeId = "";

    // Hide the component if hideArtworkLabel is true
    if (this.data.hideArtworkLabel) {
      this.el.object3D.visible = false;
    }

    this.loadQRData();
  },
  loadQRData() {
    // Don't generate QR code if label should be hidden
    if (this.data.hideArtworkLabel) {
      return;
    }

    if (!this.data.qrCodeId) {
      this.createShortID();
    } else {
      this.getQrCodeURL();
    }
  },
  async getQrCodeURL() {
    const responseShorten = await fetch(`${configs["AR_API_SHORTENER"]}/${this.data.qrCodeId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });

    const response = await responseShorten.json();
    this.arURL = response.data;
    this.initialized = true;
    this.qrCodeId = this.data.qrCodeId;
    this.createScene();
  },
  async createShortID() {
    const prepareData = {
      src: this.data.glbModel,
      ar: true,
      "ar-modes": "webxr scene-viewer quick-look",
      "camera-controls": true,
      "shadow-intensity": 1
    };

    const urlModel = `${configs["AR_EDITOR"]}/editor/view/?data=${encodeURI(JSON.stringify(prepareData))}`;

    const responseShorten = await fetch(`${configs["AR_API_SHORTENER"]}/shorten`, {
      method: "POST",
      body: JSON.stringify({ data: urlModel }),
      headers: {
        "Content-Type": "application/json"
      }
    });

    const { data: snippetCode } = await responseShorten.json();
    this.qrCodeId = snippetCode;
    this.initialized = true;
    this.createScene();
  },
  update(oldData) {
    if (this.data.remoteArtwork != oldData.remoteArtwork) {
      this.createScene("remoteArtwork");
    }

    if (this.data.glbModel != oldData.glbModel) {
      this.createScene("glbModel");
    } else if (this.data.frame != oldData.frame) {
      this.createScene("frame");
    }
  },
  async createQRCodeImage(url = "") {
    if (!this.qrCodeUrl) {
      const qrCodeDataUrl = await QRCode.toDataURL(url);
      this.qrCodeUrl = qrCodeDataUrl;
    }

    return new Promise(resolve => {
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(this.qrCodeUrl, texture => {
        const planeGeometry = new THREE.PlaneGeometry(0.42, 0.42);
        const planeMaterial = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        resolve(plane);
      });
    });
  },
  async generateARUrl(url) {
    const prepareData = {
      src: url,
      ar: true,
      "ar-modes": "webxr scene-viewer quick-look",
      "camera-controls": true,
      "shadow-intensity": 1
    };

    const urlModel = `${configs["AR_EDITOR"]}/editor/view/?data=${encodeURI(JSON.stringify(prepareData))}`;
    const responseShorten = await fetch(`${configs["AR_API_SHORTENER"]}/${this.qrCodeId}`, {
      method: "PUT",
      body: JSON.stringify({ origUrl: urlModel }),
      headers: {
        "Content-Type": "application/json"
      }
    });

    await responseShorten.json();
    const arURL = `${configs["AR_EDITOR"]}/view/${this.qrCodeId}`;
    return arURL;
  },
  async ArtworkObjectUrl() {
    const blob = await getModelOnly({
      imageUrl: this.data.image,
      frameUrl: this.data.frame,
      artworkInfo: this.data.artworkInfo,
      mediumName: this.data.mediumName,
      glbModel: this.data.glbModel,
      qrCodeId: this.qrCodeId,
      scaleXArtwork: this.data.scaleX,
      scaleYArtwork: this.data.scaleY,
      scaleZ: this.data.scaleZ,
      isImage: !!this.data.image,
      resizeOnInit: this.data.resizeOnInit,
      initialScaleX: this.data.initialScaleX,
      initialScaleY: this.data.initialScaleY,
      initialScaleZ: this.data.initialScaleZ,
      scaleFactorX: this.data.scaleFactorX,
      scaleFactorY: this.data.scaleFactorY,
      scaleFactorZ: this.data.scaleFactorZ,
      myMediaId: this.data.myMediaId,
      isDigitalFile: this.data.isDigitalFile
    });

    //dashbardUrl
    // const marketPlaceApiTenant = configs["AR_MARKETPLACE_TENANT"];

    const folder = "ntfs/qr_code";

    const postData = {
      name: `${crypto.randomUUID()}-${"file.glb"}`,
      type: "model/gltf-binary"
    };

    const glbFile = new File([blob], postData.name, {
      type: postData.type
    });

    const signedUrl = await getSignedUrl({ file: glbFile, folder });

    await fetch(signedUrl.presigned_url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/octet-stream",
        accept: "application/json",
        "x-ms-blob-type": "BlockBlob"
      },
      body: glbFile
    });

    const urlS3Model = signedUrl.download_url;

    return urlS3Model;
  },
  updateScale() {
    const box = createBoundingBoxFromObject(this.el.parentElement.object3DMap.mesh);
    let valueX = (box.max.x - box.min.x) / 2 + 0.43;
    let valueY = (box.max.y - box.min.y) / 2 - 0.43;
    this.el.setAttribute("position", `${valueX} ${valueY} 0`);
  },
  async createScene(source = "") {
    const ancestorEl = findAncestorWithComponent(this.el, "media-loader");
    try {
      if (!this.qrCodeId) {
        return;
      }

      const fileUrl = this.data.completed ? this.data.remoteArtwork : await this.ArtworkObjectUrl();
      if (!this.arUrl) {
        this.arURL = await this.generateARUrl(fileUrl);

        if (ancestorEl && ancestorEl.components && ancestorEl.components["media-loader"]) {
          ancestorEl.components["media-loader"].data.version = ancestorEl.components["media-loader"].data.version + 1;
          ancestorEl.components["media-loader"].data.src = fileUrl;
        }

        // si tiene un artworkId entonces se actualiza
      } else {
        this.arURL = null;
      }

      this.handlePresentedArtwork();
      this.el.parentEl.emit("loaded-qr");
      window.dispatchEvent(new CustomEvent("loaded-qr"));
    } catch (e) {
      //
    }
  },

  createBoundingBoxFromObject(obj) {
    return new THREE.Box3().setFromObject(obj);
  },
  handlePresentedArtwork() {
    const ancestorEl = findAncestorWithComponent(this.el, "artwork");
    ancestorEl.components["artwork"].handlePresentedArtwork();
  }
});
